import BasicButton from "@/components/core/buttons/BasicButton";
import UploadImageField from "@/components/core/inputs/UploadImageField";
import FormDetailItem from "@/components/core/items/FormDetailItem";
import InfoModal from "@/components/core/modals/InfoModal";
import {
  requestDeleteOfficePicture,
  requestUpdateOfficeAlias,
  requestUpdateOfficePicture,
} from "@/state/offices/OfficesEffects";
import { Image } from "@/type/core/image/image.types";
import { Office } from "@/type/core/offices/offices.types";
import { OfficeUpdatePictureFormData } from "@/utils/OfficeUpdatePictureFormData";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import { Input } from "antd";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicLink from "@/components/core/links/BasicLink";
import { requestGetPictureFromUrl } from "@/state/picture/PictureEffects";
import { getToken } from "@/utils/rest/RestClient";
import { blobToBase64 } from "@/utils/helpers/base64-helper";
import configuration from "@/utils/Config";
import { setOfficeAlias } from "@state/auth/AuthEvents";
import { ROUTES } from "@routes/Routes";
import { requestGetUserInfo } from "@/state/auth/AuthEffects";

interface OfficeDescriptionProps {
  office: Office;
}

const OfficeDescription: FunctionComponent<OfficeDescriptionProps> = ({
  office,
}) => {
  const officeId = office.id;
  const showIsExpertComptable = office.expertComptable;
  const isCollaborateur = office.collaborateur;

  const { t } = useTranslation();
  const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(false);
  const [avatar, setAvatar] = useState<Image | undefined>();
  const [alias, setAlias] = useState<string>(office.alias);

  const handleSubmitAvatarUpdate = (image?: Image) => {
    if (!image) {
      void requestDeleteOfficePicture({
        dto: { picture: true },
        id: officeId,
      })
        .then(() => {
          void requestGetUserInfo({});
          toastSuccess(t("success.officePicture.delete"));
        })
        .catch(() => {
          toastError(t("error.toast.officePicture.delete"));
        })
        .finally(() => {
          setAvatar(image);
        });
    } else {
      const officeUpdataPictureFormData = new OfficeUpdatePictureFormData(
        image?.file,
      );
      void requestUpdateOfficePicture({
        dto: officeUpdataPictureFormData.get(),
        id: officeId,
      })
        .then(() => {
          void requestGetUserInfo({});
          toastSuccess(t("success.officePicture.update"));
        })
        .catch(() => {
          toastError(t("error.toast.officePicture.delete"));
        })
        .finally(() => {
          setAvatar(image);
        });
    }
  };

  const handleUpdateAlias = () => {
    const id = office.id;
    void requestUpdateOfficeAlias({
      dto: { id, alias },
      id,
    })
      .then(() => {
        toastSuccess(t("success.officePicture.alias"));
        setOfficeAlias({ id: id, newAlias: alias });
      })
      .catch(() => {
        toastError(t("error.toast.officePicture.alias"));
      });
  };

  useEffect(() => {
    if (
      office.picture &&
      office.picture.length > 0 &&
      office.picture[0].picture
    ) {
      setIsAvatarLoading(true);
      requestGetPictureFromUrl(
        `${configuration.backendBaseUrl}${office.picture[0].picture}&token=${getToken()}`,
      )
        .then((result) => {
          if (result.ok && result.data) {
            const blob = result.data;
            if (blob) {
              blobToBase64(blob)
                .then((base64Data) => {
                  const picture: Image = {
                    base64Data,
                    file: blob,
                    name: "officePicture",
                    type: "png",
                  };
                  setAvatar(picture);
                })
                .catch(() => {
                  toastError("Failed to convert blob to base64");
                })
                .finally(() => setIsAvatarLoading(false));
            }
          }
        })
        .catch(() => {
          toastError("Error fetching picture");
        })
        .finally(() => setIsAvatarLoading(false));
    }
  }, [office.picture]);

  return (
    <>
      <FormDetailItem
        title={t("offices.office.officePicture")}
        description={
          <>
            <UploadImageField
              field="picture"
              className="mb-0"
              imageContent={avatar}
              imageContentLoading={isAvatarLoading}
              setImage={(updatedAvatar) =>
                handleSubmitAvatarUpdate(updatedAvatar)
              }
            />
          </>
        }
        className="align-items-center mb-16"
      />
      <FormDetailItem
        title={t("offices.office.name")}
        description={<>{office.name}</>}
        className="mb-16"
      />
      <FormDetailItem
        title={
          <>
            {t("offices.office.alias")}
            <InfoModal value="alias" info />
            {" :"}
          </>
        }
        description={
          <>
            {isCollaborateur && !showIsExpertComptable && <>{office.alias}</>}
            {showIsExpertComptable && (
              <div className="d-flex align-items-center gap-32">
                <Input
                  defaultValue={office.alias}
                  maxLength={150}
                  value={alias}
                  onChange={(event) => setAlias(event.target.value)}
                />
                {office.alias != alias && (
                  <BasicButton
                    text={t("offices.office.modify")}
                    onClick={handleUpdateAlias}
                    disabled={alias.length < 1}
                  />
                )}
              </div>
            )}
          </>
        }
        className="mb-16"
      />
      <FormDetailItem
        title={t("offices.office.zipCode")}
        description={<>{office.zipCode}</>}
        className="mb-16"
      />
      <FormDetailItem
        title={t("offices.office.city")}
        description={<>{office.city}</>}
        className="mb-16"
      />
      <FormDetailItem
        title={t("offices.office.siret")}
        description={<>{office.siret}</>}
        className="mb-16"
      />
      <FormDetailItem
        title={t("offices.office.cguAndRgpd")}
        description={
          <span
            className={`d-flex align-items-center ${
              office.cgu ? "text-success" : "text-danger"
            }`}
          >
            {office.cgu ? (
              <>
                <CheckIcon className="icon-30" />
                <span>
                  {`${t("offices.office.cgu.accepted")} ${t(
                    "offices.office.cgu.by",
                  )}`}
                  <strong>{office.cguDetail?.acceptedBy}</strong>
                  {`${t("offices.office.cgu.the")}${
                    office.cguDetail?.acceptedDate
                  }`}
                </span>
              </>
            ) : (
              <>
                <CrossIcon className="icon-30 text-danger" />
                <span>{t("offices.office.cgu.notAccepted")}</span>
                <BasicLink
                  to={ROUTES.extranet.menu.offices.cgu.generate()}
                  className="ml-4"
                >
                  {t("offices.office.cgu.accept")}
                </BasicLink>
              </>
            )}
          </span>
        }
        className="mb-16"
      />
    </>
  );
};

export default OfficeDescription;
